import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useZelleOrder } from "./Zelle/useZelleOrder";
import { FormProvider } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";

interface ZelleCheckoutProps {
  product?: any;
  isDisabled?: boolean;
}

const ZelleCheckout = ({ product, isDisabled = false }: ZelleCheckoutProps) => {
  const { form, onSubmit, isSubmitting } = useZelleOrder();
  const [copied, setCopied] = useState(false);
  const zelleEmail = "colin@battleforgepc.com";
  
  const handleCopyEmail = () => {
    navigator.clipboard.writeText(zelleEmail);
    setCopied(true);
    
    setTimeout(() => {
      setCopied(false);
    }, 3000);
  };
  
  // Use the product prop if available (for direct product purchase)
  // or get cart contents if it's a cart checkout

  return (
    <div className="mt-6 space-y-4">
      {isDisabled ? (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-center">
          <AlertTriangle className="h-10 w-10 text-red-400 mx-auto mb-2" />
          <h3 className="font-medium text-lg text-white mb-1">Checkout Restricted</h3>
          <p className="text-gray-400 text-sm">
            Your account is currently banned from making purchases.
            Please contact support for assistance.
          </p>
        </div>
      ) : (
        <>
          <div className="border border-green-500/30 bg-green-500/10 rounded-lg p-4 text-center">
            <h3 className="font-medium text-lg text-white mb-1">Pay via Zelle</h3>
            <p className="text-gray-400 text-sm">
              Send payment to the email below, then complete your order.
            </p>
            
            <div className="mt-4 flex flex-col items-center">
              <div className="bg-gaming-dark rounded-lg px-4 py-3 font-mono text-lg text-gaming-blue w-fit mb-2">
                {zelleEmail}
              </div>
              
              <Button onClick={handleCopyEmail} variant="secondary" className="mt-2">
                {copied ? "Copied!" : "Copy Email"}
              </Button>
            </div>
          </div>
          
          <div className="space-y-4">
            <Separator />
            <div className="space-y-2">
              <h4 className="font-medium">Steps to complete your order:</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-300">
                <li>Open your Zelle app or banking app with Zelle</li>
                <li>Send payment to <span className="text-gaming-blue">{zelleEmail}</span></li>
                <li>Include your name and email in the memo</li>
                <li>Click "Complete Order" below after sending payment</li>
              </ol>
            </div>
            
            <FormProvider {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <FormField
                    control={form.control}
                    name="customer_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Full Name</FormLabel>
                        <FormControl>
                          <Input placeholder="John Doe" {...field} className="bg-gaming-dark border-gaming-light-gray/40" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="customer_email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input placeholder="youremail@example.com" {...field} className="bg-gaming-dark border-gaming-light-gray/40" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <Separator className="my-4" />
                <h3 className="font-medium text-gaming-blue mb-2">Shipping Address</h3>
                <FormField
                  control={form.control}
                  name="address.street"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Street Address</FormLabel>
                      <FormControl>
                        <Input placeholder="123 Main St" {...field} className="bg-gaming-dark border-gaming-light-gray/40" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="address.city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City</FormLabel>
                        <FormControl>
                          <Input placeholder="New York" {...field} className="bg-gaming-dark border-gaming-light-gray/40" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="address.state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>State</FormLabel>
                        <FormControl>
                          <Input placeholder="NY" {...field} className="bg-gaming-dark border-gaming-light-gray/40" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="address.zip"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>ZIP Code</FormLabel>
                        <FormControl>
                          <Input placeholder="10001" {...field} className="bg-gaming-dark border-gaming-light-gray/40" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <Button
                  className="w-full mt-6 bg-gaming-blue hover:bg-gaming-blue/80 text-white"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Processing..." : "Complete Order"}
                </Button>
              </form>
            </FormProvider>
            
            <p className="text-xs text-gray-500 text-center">
              Your order will be processed after payment confirmation (usually within 1 business day).
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default ZelleCheckout;
